(ns ui.agent.page
  "The Agents page, `/ui/agents`: the agents of the organization, one
   per client or job, each with its own chat. What they spend at the
   models is metered hourly against the credits and shows on the
   credits' ledger, not here. Creating one opens `ui.agent.create-modal`; the chat gets
   ready in the background and the page polls until it is. The person
   never sees how: no machine, no setup, no connection to renew."
  (:require [parts.replicant.query :as query]
            [storrito.ui.core :as ui]
            [storrito.ui.table :as ui-table]
            [ui.app-frame :as app-frame]
            [ui.elements :as elements]))

(def agents-query
  {:query/kind :query/agents})

(defn created-at-text
  [date]
  (if (inst? date)
    (.toLocaleDateString date)
    ""))

(defn delete-command
  [agent-uuid]
  {:command/kind :command/agent-delete
   :command/data {:agent/uuid (str agent-uuid)}})

(defn retry-command
  [agent-uuid]
  {:command/kind :command/agent-provision-retry
   :command/data {:agent/uuid (str agent-uuid)}})

(defn provisioning?
  "Whether an agent's chat is getting ready, so the page keeps asking
   for news (`:agents/poll`)."
  [agents]
  (boolean (some #(= :provisioning (:agent/status %)) agents)))

(defn try-again-button
  "Sets the agent's chat up again (`:command/agent-provision-retry`):
   after a failure, or for a chat that does not answer."
  [agent-uuid]
  [:button
   {:class ui/a-indigo
    :on {:click [[:data/command
                  (retry-command agent-uuid)
                  {:on-success [[:data/query agents-query]
                                [:agents/poll]]
                   :on-error [[:ui/show-error]]}]]}}
   "Try again"])

(defn error-text
  "Why the chat could not get ready: support asks for it."
  [{:keys [agent/error]}]
  (when (seq (str error))
    [:p {:class ["mt-1" "max-w-xl" "text-xs" "text-gray-500"]} error]))

(defn open-link
  [page-url]
  [:a {:class ui/a-indigo :href page-url :target "_blank" :rel "noopener"} "Open chat ↗"])

(defn chat
  "The state of the agent's chat: getting ready (with the step under
   way), its link, asleep (the link wakes it), not ready with a Try
   again, or a machine that is broken or on its way out."
  [{:keys [agent/uuid agent/status agent/step agent/page-url] :as agent}]
  (case status
    :provisioning
    [:div
     [:span {:class ["inline-flex" "items-center" "gap-2" "text-sm" "text-gray-700"]}
      [:span {:class ["inline-block" "h-2" "w-2" "animate-pulse" "rounded-full" "bg-indigo-500"]}]
      "Getting ready …"]
     (when (seq (str step))
       [:p {:class ["mt-1" "text-xs" "text-gray-500"]} step])]

    :failed
    [:div
     [:span {:class ["text-sm" "font-medium" "text-red-600"]} "Could not get ready. "]
     (try-again-button uuid)
     (error-text agent)]

    :ready
    (if page-url
      (open-link page-url)
      [:span {:class ["text-sm" "text-gray-700"]} "Ready"])

    :starting
    [:div
     (when page-url (open-link page-url))
     [:p {:class ["mt-1" "text-xs" "text-gray-500"]}
      "The chat does not answer yet. "
      (try-again-button uuid)]]

    :asleep
    [:div
     (when page-url (open-link page-url))
     [:p {:class ["mt-1" "text-xs" "text-gray-500"]} "Asleep: opening the chat wakes it."]]

    :broken
    [:span {:class ["text-sm" "font-medium" "text-red-600"]}
     "The machine is broken. Delete the agent and make a new one."]

    :deleting
    [:span {:class ["text-sm" "text-gray-700"]} "Being deleted …"]

    [:span {:class ["text-sm" "text-gray-700"]} "—"]))

(defn row
  [{:keys [agent/uuid agent/name agent/created-at] :as agent}]
  [:tr {:class ui-table/tr
        :replicant/key (str uuid)}
   [:td {:class (concat ui-table/td ["font-medium" "text-gray-900"])}
    name]
   [:td {:class ui-table/td} (created-at-text created-at)]
   [:td {:class (concat ui-table/td ["align-top"])}
    (chat agent)]
   [:td {:class ui-table/td}
    [:div {:class ["flex" "justify-end"]}
     [:button
      {:class ["font-semibold" "text-red-600" "hover:text-red-500"]
       :on {:click [[:js/confirm
                     (str "Delete the agent \"" name "\"? Its chat is gone; the accounts connected for the organization stay for the other agents.")]
                    [:data/command
                     (delete-command uuid)
                     {:on-success [[:data/query agents-query]]
                      :on-error [[:ui/show-error]]}]]}}
      "Delete"]]]])

(defn table
  [state]
  (let [agents (:agents (query/get-result state agents-query))]
    [:table {:class ui-table/table}
     [:thead {:class ui-table/thead}
      [:tr
       [:th {:scope "col" :class ui-table/th} "Name"]
       [:th {:scope "col" :class ui-table/th} "Created"]
       [:th {:scope "col" :class ui-table/th} "Chat"]
       [:th {:scope "col" :class ui-table/th}]]]
     [:tbody {:class ui-table/tbody}
      (if (seq agents)
        (map row agents)
        [:tr {:class ui-table/tr}
         [:td {:colSpan 4
               :class (concat ui-table/td ["font-medium"])}
          (if (query/available? state agents-query)
            "No agents yet. Create one with the button above."
            "Loading …")]])]]))

(defn panel
  [state]
  [:div
   [:div {:class "sm:flex sm:items-center"}
    [:div {:class "sm:flex-auto"}
     [:h1 {:class ui/h2} "Agents"]
     [:p {:class (into ["mt-2" "max-w-3xl"] elements/p)}
      "Think of an agent like a personal assistant. It can take over tasks that "
      "need to be done once or on a schedule, like managing the social media "
      "accounts for a brand. It is also possible to build integrations with other "
      "services, like posting an Instagram Story every time you upload a video "
      "into a given Google Drive folder."]]
    [:div {:class "mt-4 sm:ml-16 sm:mt-0"}
     [:button
      {:class ui/button-primary
       :on {:click [[:ui/modal-open :modal/agent-create {}]]}}
      "New agent"]]]
   [:div {:class "mt-8 flow-root"}
    [:div {:class "-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8"}
     [:div {:class "inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"}
      [:div {:class ui/box}
       (table state)]]]]
   [:p {:class (into ["mt-6" "max-w-3xl"] elements/p-sm)}
    "A new agent takes a few minutes to get ready; the page keeps itself up to date. "
    "In its chat the agent asks what it is for. "
    "What the agents spend at the models is charged to your credits every hour, with the same markup as the AI features; "
    "when the credits are used up, the agents stop until you top up. "
    "The services an agent connects (a Google Drive, a Gmail) are connected for the whole organization: every agent reaches them."]])

(defn render
  [state]
  (app-frame/app-frame
    state
    (panel state)))

(def page
  {:page-id :pages/agents
   :route [["ui" "agents"]]
   :on-load (fn [_]
              [[:data/query agents-query]
               [:agents/poll]])
   :render #'render})
